// PROTOTYPE ONLY. OSMIX has no ledger, no commission model and no payouts
// table — none of this is backed by anything in the gateway. The numbers here
// exist to agree a product direction with the founder role; treat every field
// as a proposal, not a report.
import { sleep } from "@/lib/utils";

import type {
	ObligationState,
	Api,
	FinanceBreakdownRow,
	FinanceExternal,
	FinanceInternal,
	FinanceObligation,
	FinancePnl,
	FinanceSettings,
	PayoutRequest,
	FinanceSummary,
	Payout,
	PayoutSchedule,
	SettlementCadence,
} from "../types";
import { rangeDays } from "./dashboard";

/** Стадии обязательств в моке — чтобы кнопки выплат работали и офлайн. */
const mockObligationState = new Map<string, ObligationState>();
import { storefronts } from "./storefronts";

/** Platform take rate assumed for the prototype. Not a real setting. */
const COMMISSION_RATE = 0.12;

const rand = (seed: number) => {
	const x = Math.sin(seed) * 10_000;
	return x - Math.floor(x);
};

const gmvSeries = (days: number) => {
	const today = new Date();
	return Array.from({ length: days }, (_, i) => {
		const date = new Date(today);
		date.setDate(today.getDate() - (days - 1 - i));
		const weekend = date.getDay() === 0 || date.getDay() === 6 ? 0.7 : 1;
		const trend = 145_000 * (1 + (0.6 * i) / days);
		return {
			date: date.toISOString().slice(0, 10),
			value: Math.round(trend * weekend * (0.8 + rand(71 + i) * 0.4)),
		};
	});
};

const CATEGORIES: [string, string, number][] = [
	["jewelry", "Украшения", 0.31],
	["ceramics", "Керамика", 0.22],
	["textile", "Текстиль", 0.18],
	["accessories", "Аксессуары", 0.16],
	["decor", "Декор", 0.13],
];

/**
 * Revenue share per shop, highest first. Shops themselves come from the
 * storefront catalog so every row keys on a REAL slug — that is what lets the
 * breakdown open the existing shop card instead of a finance-only stub.
 */
const SHOP_SHARES = [0.24, 0.19, 0.17, 0.14, 0.11];

const topShops = async (): Promise<[string, string, number][]> => {
	const shops = await storefronts.list();
	return SHOP_SHARES.map((share, i) => {
		const shop = shops[i];
		return [shop?.slug ?? `shop-${i}`, shop?.name ?? `Shop ${i + 1}`, share];
	});
};

const breakdown = (rows: [string, string, number][], gmv: number, orders: number): FinanceBreakdownRow[] =>
	rows.map(([key, label, share]) => ({
		key,
		label,
		gmv: Math.round(gmv * share),
		commission: Math.round(gmv * share * COMMISSION_RATE),
		orders: Math.round(orders * share),
	}));

/**
 * Settlements run on the 1st and the 15th — «два раза в месяц». Returns the next
 * such date on or after today.
 */
const nextSettlement = (from = new Date()): Date => {
	const day = from.getDate();
	if (day < 15) return new Date(from.getFullYear(), from.getMonth(), 15);
	return new Date(from.getFullYear(), from.getMonth() + 1, 1);
};

/**
 * Local ISO day. `toISOString()` converts to UTC first, which shifts a local
 * midnight date back a day in any positive offset — it rendered the 1st as the
 * 31st. Settlement dates are calendar days, not instants.
 */
const isoDay = (date: Date): string => {
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * What is accruing towards the next payout date. The founder plans cash against
 * this: «вот сегодня 10-е, к выводу там миллион триста».
 */
const schedule = (gmv: number, sellersCount: number): PayoutSchedule => {
	const next = nextSettlement();
	// Accrual is proportional to how far into the current half-month we are.
	const daysToNext = Math.max(1, Math.round((next.getTime() - Date.now()) / 86_400_000));
	const progress = Math.min(1, (15 - daysToNext) / 15 + 0.35);
	const due = Math.round(gmv * (1 - COMMISSION_RATE) * progress);

	const history: { date: string; amount: number }[] = [];
	let cursor = new Date(next);
	for (let i = 0; i < 4; i += 1) {
		cursor = cursor.getDate() === 1
			? new Date(cursor.getFullYear(), cursor.getMonth() - 1, 15)
			: new Date(cursor.getFullYear(), cursor.getMonth(), 1);
		history.push({
			date: isoDay(cursor),
			amount: Math.round(due * (0.82 + rand(200 + i) * 0.3)),
		});
	}

	return { nextDate: isoDay(next), amountDue: due, sellersCount, history };
};

/**
 * Period P&L. Advertising revenue is modelled even though no ad product exists
 * — the founder asked for it up front rather than retrofitted.
 */
const pnl = (gmv: number, commission: number): FinancePnl => {
	const advertising = Math.round(commission * 1.21);
	const grossEarnings = commission + advertising;
	// Acquiring ~2.4% of GMV plus a flat operating cost — invented.
	const expenses = Math.round(gmv * 0.024 + grossEarnings * 0.11);
	return {
		gmv,
		commission,
		advertising,
		grossEarnings,
		expenses,
		netRevenue: grossEarnings - expenses,
	};
};

/** Share of GMV withheld from sellers to cover delivery. Invented. */
const DELIVERY_RATE = 0.058;

/** Carriers the platform settles with. Cadence is theirs to dictate, not ours. */
const CARRIERS: [string, string, SettlementCadence, number][] = [
	["cdek", "СДЭК", "daily", 0.62],
	["post", "Почта России", "weekly", 0.24],
	["boxberry", "Boxberry", "weekly", 0.14],
];

/**
 * The external contour: everything owed to somebody else. Split three ways —
 * accrued, still blocked until the period closes, and payable now — because
 * «к выводу» alone hides the difference the founder kept asking about.
 */
const external = (
	gmv: number,
	shops: [string, string, number][],
	nextDate: string,
): FinanceExternal => {
	const accrued = Math.round(gmv * (1 - COMMISSION_RATE));
	// Two-week cycle: how much of it has already cleared its period.
	const available = Math.round(accrued * 0.68);
	const paid = Math.round(accrued * 0.21);
	const blocked = accrued - available - paid;

	const withheld = Math.round(gmv * DELIVERY_RATE);
	const deliveryPaid = Math.round(withheld * 0.74);

	const sellerObligations: FinanceObligation[] = shops.map(([slug, name, share], i) => {
		const shopAccrued = Math.round(gmv * share * (1 - COMMISSION_RATE));
		const shopPaid = Math.round(shopAccrued * (0.15 + rand(310 + i) * 0.3));
		return {
			key: slug,
			label: name,
			kind: "seller",
			accrued: shopAccrued,
			paid: shopPaid,
			due: shopAccrued - shopPaid,
			nextDate,
			cadence: "fortnightly",
			state: mockObligationState.get(slug) ?? "formed",
			documents: [],
		};
	});

	const carrierObligations: FinanceObligation[] = CARRIERS.map(
		([key, label, cadence, share], i) => {
			const carrierAccrued = Math.round(withheld * share);
			const carrierPaid = Math.round(carrierAccrued * (0.6 + rand(420 + i) * 0.35));
			return {
				key,
				label,
				kind: "carrier",
				accrued: carrierAccrued,
				paid: carrierPaid,
				due: carrierAccrued - carrierPaid,
				// A daily carrier is owed tomorrow, not on the platform's cycle.
				nextDate: cadence === "daily" ? isoDay(new Date(Date.now() + 86_400_000)) : nextDate,
				cadence,
				state: mockObligationState.get(key) ?? "formed",
				documents: [],
			};
		},
	);

	return {
		gmv,
		accrued,
		blocked,
		available,
		paid,
		delivery: { withheld, payable: withheld - deliveryPaid, paid: deliveryPaid },
		obligations: [...sellerObligations, ...carrierObligations],
	};
};

/**
 * The internal contour: what the platform earned. Costs are excluded on
 * purpose — see the note on FinanceInternal.
 */
const internal = (commission: number, advertising: number): FinanceInternal => ({
	commission,
	advertising,
	income: commission + advertising,
});

const PAYOUT_STATUSES: Payout["status"][] = ["pending", "processing", "paid", "paid", "failed"];

const payouts = (shops: [string, string, number][], gmv: number): Payout[] =>
	shops.map(([shopId, shopName, share], i) => {
		const created = new Date();
		created.setDate(created.getDate() - i * 3);
		return {
			id: `pay_${shopId}`,
			shopId,
			shopName,
			// What the seller is owed: their GMV minus the platform's cut.
			amount: Math.round(gmv * share * (1 - COMMISSION_RATE)),
			status: PAYOUT_STATUSES[i % PAYOUT_STATUSES.length] ?? "pending",
			period: created.toISOString().slice(0, 7),
			createdAt: created.toISOString(),
		};
	});

// Зеркало денежных настроек гейтвея для офлайн-работы над консолью.
const financeSettings: FinanceSettings = {
	commissionRate: COMMISSION_RATE,
	commissionByCategory: {},
	commissionByShop: {},
	settlementDays: 14,
	currency: "RUB",
	carrierName: "СДЭК",
	carrierCadence: "daily",
};

const mockPayoutRequests: PayoutRequest[] = [
	{
		id: "pay_demo_1",
		shopId: "shop-handmade",
		shopName: "Мастерская Анна",
		period: "2026-08",
		amount: 128_400,
		status: "new",
		sellerComment: "Прошу на реквизиты из анкеты",
		createdAt: new Date(Date.now() - 2 * 86_400_000).toISOString(),
		// Снимок на момент заявки: доступно было больше, чем запрошено —
		// остаток продавец не забрал.
		accruedAtRequest: 128_400,
		availableAtRequest: 128_400,
		notRequested: 0,
	},
	{
		id: "pay_demo_2",
		shopId: "shop-ceramics",
		shopName: "Гончарная мастерская",
		period: "2026-08",
		amount: 30_000,
		status: "approved",
		createdAt: new Date(Date.now() - 4 * 86_400_000).toISOString(),
		decidedAt: new Date(Date.now() - 86_400_000).toISOString(),
		accruedAtRequest: 52_300,
		availableAtRequest: 52_300,
		notRequested: 22_300,
	},
];

const MOCK_RECIPIENT = {
	name: "Гончаров И. П. (НПД)",
	inn: "770987654321",
	kpp: "",
	account: "40817810099910004321",
	bik: "044525225",
	bankName: "ПАО Сбербанк",
	corrAccount: "30101810400000000225",
};

/** Офлайн-файл: в моке 1С-выгрузка — просто текст, без реквизитов площадки. */
/** Продавцы мок-бухгалтерии. */
const MOCK_ACC_SELLERS = [
	{ shopId: "shop-natur", shopName: "НатурТекстиль", legalName: "ИП Хайруллина Г. Р.", inn: "165912345678", kpp: "", vat: "ИП, режим НДС не указан" },
	{ shopId: "shop-handmade", shopName: "Мастерская Анны", legalName: "ИП Мастерова А. С.", inn: "781234567890", kpp: "", vat: "без НДС" },
	{ shopId: "shop-artcraft", shopName: "ArtCraft", legalName: "Кожевников Д. А. (НПД)", inn: "667812345609", kpp: "", vat: "НПД (самозанятый), без НДС" },
];

const mockFile = (name: string, text: string, type: string) => ({
	blob: new Blob([text], { type }),
	filename: name,
	rows: 1,
	skipped: 0,
});

export const finances: Api["finances"] = {
	async getSettings() {
		await sleep(120);
		return {
			...financeSettings,
			commissionByCategory: { ...financeSettings.commissionByCategory },
			commissionByShop: { ...financeSettings.commissionByShop },
		};
	},
	async updateSettings(patch) {
		await sleep(180);
		if (patch.commissionRate !== undefined) financeSettings.commissionRate = patch.commissionRate;
		if (patch.commissionByCategory) financeSettings.commissionByCategory = { ...patch.commissionByCategory };
		if (patch.commissionByShop) financeSettings.commissionByShop = { ...patch.commissionByShop };
		if (patch.settlementDays !== undefined) financeSettings.settlementDays = patch.settlementDays;
		return this.getSettings();
	},
	// Мок жизненного цикла: держит стадии в памяти, чтобы UI-работу можно было
	// вести офлайн. Источник истины — гейтвей.
	// Офлайн-зеркало воронки выплат.
	async payouts(status = "all") {
		await sleep(150);
		return status === "all"
			? mockPayoutRequests
			: mockPayoutRequests.filter((r) => r.status === status);
	},
	async payoutPage(status, page, pageSize) {
		await sleep(150);
		const rows =
			status === "all" ? mockPayoutRequests : mockPayoutRequests.filter((r) => r.status === status);
		const start = (page - 1) * pageSize;
		return {
			items: rows.slice(start, start + pageSize),
			total: rows.length,
			page,
			pageSize,
			totalPages: Math.ceil(rows.length / pageSize),
		};
	},
	async payoutDetails(id) {
		await sleep(150);
		const request = mockPayoutRequests.find((r) => r.id === id);
		if (!request) throw new Error("Заявка на выплату не найдена");
		const sales = 146_000;
		const commission = 14_600;
		const refunds = 3_000;
		return {
			request,
			statement: {
				period: request.period,
				sales,
				commission,
				refunds,
				accrued: sales - commission - refunds,
				paid: 0,
				pending: request.amount,
				available: 0,
			},
			orders: [
				{ id: "ord_demo_1", createdAt: "2026-08-04T10:00:00.000Z", status: "completed", goods: 98_000, refunded: 0 },
				{ id: "ord_demo_2", createdAt: "2026-08-19T15:30:00.000Z", status: "delivered", goods: 48_000, refunded: 3_000 },
			],
			sellerUserId: "usr_seed_anna",
		};
	},
	async decidePayout(id, input) {
		await sleep(180);
		const request = mockPayoutRequests.find((r) => r.id === id);
		if (!request) throw new Error("Заявка на выплату не найдена");
		request.status = input.status;
		if (input.comment) request.adminComment = input.comment;
		request.decidedAt = new Date().toISOString();
		if (input.status === "paid") request.paidAt = request.decidedAt;
		return request;
	},
	async payoutRegistry() {
		await sleep(150);
		const lines = mockPayoutRequests
			.filter((r) => r.status === "approved")
			.map((request) => ({
				request,
				recipient: MOCK_RECIPIENT,
				problems: [],
				ready: true,
				purpose: `Перечисление денежных средств продавцу по заявке ${request.id}. Без налога (НДС)`,
			}));
		const total = lines.reduce((s, l) => s + l.request.amount, 0);
		return {
			lines,
			payer: null,
			missingPayerEnv: ["OSMIX_PAYER_NAME", "OSMIX_PAYER_INN", "OSMIX_PAYER_ACCOUNT"],
			total,
			readyTotal: total,
		};
	},
	async exportPayoutRegistry(format) {
		await sleep(150);
		if (format === "1c") {
			throw new Error("Мок: реквизиты площадки не заданы — файл для 1С собирает только шлюз");
		}
		return mockFile("OSMIX реестр выплат.csv", "Мок-реестр", "text/csv");
	},
	async cashFlow(from, to) {
		await sleep(150);
		const rows = [
			{ date: `${from}T09:00:00.000Z`, section: "receipts" as const, direction: 1 as const, counterparty: "Покупатель", document: "Заказ ord_demo_1", amount: 98_000, comment: "mock" },
			{ date: `${from}T09:00:00.000Z`, section: "commission" as const, direction: 0 as const, counterparty: "Мастерская Анна", document: "Заказ ord_demo_1", amount: 11_760, comment: "" },
		];
		return {
			from,
			to,
			rows,
			totals: { receipts: 98_000, refunds: 0, payouts: 0, commission: 11_760, net: 98_000 },
			byMonth: [{ month: from.slice(0, 7), receipts: 98_000, refunds: 0, payouts: 0, commission: 11_760, net: 98_000 }],
		};
	},
	async exportCashFlow(from, to, format) {
		await sleep(150);
		return mockFile(`OSMIX ДДС ${from}-${to}.${format}`, "Мок-ДДС", "text/csv");
	},
	async accountingShops() {
		await sleep(80);
		return MOCK_ACC_SELLERS.map((s) => ({ shopId: s.shopId, name: s.shopName }));
	},
	async accountingSales(query) {
		await sleep(120);
		const k = query.shopId ? 0.3 : 1;
		return {
			from: query.from ?? "",
			to: query.to ?? "",
			linesTotal: Math.round(174 * k),
			totals: {
				orders: Math.round(61 * k),
				qty: Math.round(175 * k),
				goods: Math.round(1_079_431 * k),
				sellerDiscount: 0,
				promoDiscount: 0,
				delivery: Math.round(28_420 * k),
				paidByBuyer: Math.round(1_107_851 * k),
				commission: Math.round(129_531.72 * k * 100) / 100,
				returned: 1_138,
			},
		};
	},
	async accountingReturns(query) {
		await sleep(120);
		const seller = MOCK_ACC_SELLERS[0]!;
		return {
			from: query.from ?? "",
			to: query.to ?? "",
			lines: [
				{ returnId: "ret_demo_1", orderId: "ord_demo_1", status: "refunded", requestedAt: "2026-07-14T09:00:00.000Z", refundedAt: "2026-07-19T09:00:00.000Z", seller, items: "Шёлковый платок «Маки» (70x70 см) × 1", total: 1_138 },
				{ returnId: "ret_demo_2", orderId: "ord_demo_2", status: "requested", requestedAt: "2026-07-22T09:00:00.000Z", seller, items: "", total: 0 },
			],
			totals: { count: 2, refunded: 1, goods: 1_138, delivery: 0, total: 1_138 },
		};
	},
	async agentReports(query) {
		await sleep(150);
		const period = query.period ?? "2026-07";
		return MOCK_ACC_SELLERS.filter((s) => !query.shopId || s.shopId === query.shopId).map((seller, i) => {
			const sales = [51_624, 124_118, 30_359][i] ?? 10_000;
			const commission = Math.round(sales * 12) / 100;
			const accrued = Math.round((sales - commission) * 100) / 100;
			const paid = i === 2 ? 0 : Math.round(sales / 2);
			return {
				number: `ОА-${period.replace("-", "")}-${seller.shopId.toUpperCase()}`,
				period,
				date: "31.07.2026",
				seller,
				agentMissing: ["OSMIX_PAYER_NAME", "OSMIX_PAYER_INN"],
				ordersCount: 5 + i,
				summary: {
					opening: 0,
					sales,
					refunds: 0,
					commission,
					expenses: 0,
					penalties: 0,
					accrued,
					paidForPeriod: paid,
					paidInMonth: 0,
					pending: 0,
					due: Math.round((accrued - paid) * 100) / 100,
					closing: accrued,
					deliveryTransit: 1_750,
					ordersCount: 5 + i,
				},
			};
		});
	},
	async commissionActs(query) {
		const reports = await finances.agentReports(query);
		return reports.map((r) => ({
			number: r.number,
			period: r.period,
			seller: r.seller,
			sales: r.summary.sales,
			refunds: r.summary.refunds,
			commission: r.summary.commission,
			vatLabel: "Без НДС",
			vat: 0,
		}));
	},
	async settlements(query) {
		const reports = await finances.agentReports({ period: query.from, shopId: query.shopId });
		const rows = reports.map((r) => ({
			shopId: r.seller.shopId,
			seller: r.seller,
			opening: r.summary.opening,
			accrued: r.summary.accrued,
			sales: r.summary.sales,
			commission: r.summary.commission,
			refunds: r.summary.refunds,
			paid: r.summary.paidInMonth,
			closing: r.summary.closing,
			pending: r.summary.pending,
		}));
		const total = (k: keyof (typeof rows)[number]) =>
			Math.round(rows.reduce((s, r) => s + (r[k] as number), 0) * 100) / 100;
		return {
			from: query.from ?? "",
			to: query.to ?? "",
			rows,
			totals: {
				opening: total("opening"),
				accrued: total("accrued"),
				sales: total("sales"),
				commission: total("commission"),
				refunds: total("refunds"),
				paid: total("paid"),
				closing: total("closing"),
				pending: total("pending"),
			},
		};
	},
	async accountingJournal() {
		await sleep(80);
		return [
			{ id: "exp_demo_1", dataset: "acc-agent-report", format: "xlsx", filters: { period: "2026-07" }, rows: 8, createdAt: "2026-09-25T10:15:00.000Z", who: "Галина Бухова" },
			{ id: "exp_demo_2", dataset: "acc-sales", format: "xlsx", filters: { from: "2026-07-01", to: "2026-07-31" }, rows: 174, createdAt: "2026-09-25T10:12:00.000Z", who: "Галина Бухова" },
		];
	},
	async exportAccounting(dataset, query, format) {
		await sleep(150);
		return mockFile(`OSMIX ${dataset} ${query.period ?? query.from ?? ""}.${format === "html" || format === "xml" ? format : "csv"}`, "Мок-выгрузка: файл собирает шлюз", format === "html" ? "text/html" : "text/csv");
	},
	async advanceObligation(id, state) {
		await sleep(150);
		mockObligationState.set(id, state);
		return { id, state };
	},
	async generateDocuments(id) {
		await sleep(150);
		mockObligationState.set(id, "documented");
		const createdAt = new Date().toISOString();
		const period = createdAt.slice(0, 7);
		return [
			{ id: `doc_agent_${id}`, kind: "agent-report", period, createdAt },
			{ id: `doc_act_${id}`, kind: "commission-act", period, createdAt },
		];
	},
	async summary(range): Promise<FinanceSummary> {
		await sleep(300);
		const days = rangeDays(range);
		const shops = await topShops();
		const series = gmvSeries(days);
		const gmv = series.reduce((sum, p) => sum + p.value, 0);
		const commission = Math.round(gmv * COMMISSION_RATE);
		const orders = Math.round(gmv / 4_800);
		const refunds = Math.round(gmv * 0.031);
		const statement = pnl(gmv, commission);
		const plan = schedule(gmv, shops.length);

		return {
			range,
			currency: "RUB",
			external: external(gmv, shops, plan.nextDate),
			internal: internal(commission, statement.advertising),
			advertising: { value: statement.advertising, delta: 0.264 },
			pnl: statement,
			schedule: plan,
			gmv: { value: gmv, delta: 0.184 },
			commission: { value: commission, delta: 0.184 },
			// Same definition as the P&L line — two different "net revenue" numbers
			// under the same label on one page is a reporting bug, not a nuance.
			netRevenue: { value: statement.netRevenue, delta: 0.171 },
			aov: { value: Math.round(gmv / Math.max(orders, 1)), delta: 0.042 },
			refunds: { value: refunds, delta: -0.058 },
			gmvSeries: series,
			commissionSeries: series.map((p) => ({
				date: p.date,
				value: Math.round(p.value * COMMISSION_RATE),
			})),
			byCategory: breakdown(CATEGORIES, gmv, orders),
			byShop: breakdown(shops, gmv, orders),
			payouts: payouts(shops, gmv),
		};
	},
};
